import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  User,
  signInWithPhoneNumber,
  linkWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  ApplicationVerifier,
  deleteUser,
} from 'firebase/auth';
import { Platform } from 'react-native';

import { getFirebaseAuth } from '../config/getFirebaseAuth';

let pendingPhoneConfirmation: ConfirmationResult | null = null;
let pendingPhoneLinkConfirmation: ConfirmationResult | null = null;
let webRecaptcha: RecaptchaVerifier | null = null;

export function subscribeAuth(callback: (user: User | null) => void) {
  return onAuthStateChanged(getFirebaseAuth(), callback);
}

export async function registerWithEmail(email: string, password: string) {
  const cred = await createUserWithEmailAndPassword(
    getFirebaseAuth(),
    email.trim(),
    password,
  );
  return cred.user;
}

export async function signInWithEmail(email: string, password: string) {
  const cred = await signInWithEmailAndPassword(
    getFirebaseAuth(),
    email.trim(),
    password,
  );
  return cred.user;
}

export async function signInAsGuest() {
  const cred = await signInAnonymously(getFirebaseAuth());
  return cred.user;
}

export async function signOutUser() {
  pendingPhoneConfirmation = null;
  pendingPhoneLinkConfirmation = null;
  webRecaptcha = null;
  await signOut(getFirebaseAuth());
}

/** Firebase Authentication kullanıcı kaydını siler (Firestore ayrı silinmeli). */
export async function deleteCurrentAuthUser(): Promise<void> {
  const auth = getFirebaseAuth();
  const u = auth.currentUser;
  if (!u) {
    throw new Error('Oturum yok');
  }
  await deleteUser(u);
}

/** Web: SMS göndermeden önce görünmez reCAPTCHA kurulumu (container DOM id) */
export function setupWebRecaptcha(containerId: string = 'recaptcha-container') {
  if (Platform.OS !== 'web') {
    return;
  }
  const auth = getFirebaseAuth();
  if (webRecaptcha) {
    webRecaptcha.clear();
    webRecaptcha = null;
  }
  webRecaptcha = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
  });
}

/** Telefon E.164 formatında olmalı (örn +905551234567) */
export async function sendPhoneVerificationCode(phoneE164: string) {
  const auth = getFirebaseAuth();
  if (Platform.OS === 'web') {
    if (!webRecaptcha) {
      throw new Error('Önce setupWebRecaptcha() çağırın ve DOM’da container bulunsun.');
    }
    pendingPhoneConfirmation = await signInWithPhoneNumber(
      auth,
      phoneE164,
      webRecaptcha as ApplicationVerifier,
    );
    return;
  }
  // Native: Play Integrity / APNs yapılandırmasına bağlı; başarısız olursa @react-native-firebase/auth düşünün.
  pendingPhoneConfirmation = await signInWithPhoneNumber(
    auth,
    phoneE164,
    // Bazı native sürümlerde dahili doğrulayıcı kullanılır; tip uyumu için:
    undefined as unknown as ApplicationVerifier,
  );
}

export async function confirmPhoneVerificationCode(smsCode: string) {
  if (!pendingPhoneConfirmation) {
    throw new Error('Önce SMS kodu gönderilmedi veya oturum süresi doldu.');
  }
  await pendingPhoneConfirmation.confirm(smsCode.trim());
}

/** Mevcut e-posta hesabına telefon bağlamak (OTP sonrası `confirmLinkPhoneVerificationCode`). */
export async function sendLinkPhoneVerification(user: User, phoneE164: string) {
  pendingPhoneLinkConfirmation = null;
  if (Platform.OS === 'web') {
    setupWebRecaptcha('recaptcha-container-profile');
    if (!webRecaptcha) {
      throw new Error('reCAPTCHA hazırlanamadı; sayfayı yenileyip tekrar deneyin.');
    }
    pendingPhoneLinkConfirmation = await linkWithPhoneNumber(
      user,
      phoneE164.trim(),
      webRecaptcha as ApplicationVerifier,
    );
    return;
  }
  pendingPhoneLinkConfirmation = await linkWithPhoneNumber(
    user,
    phoneE164.trim(),
    undefined as unknown as ApplicationVerifier,
  );
}

export async function confirmLinkPhoneVerificationCode(smsCode: string) {
  if (!pendingPhoneLinkConfirmation) {
    throw new Error('Önce telefon doğrulaması başlatılmadı.');
  }
  await pendingPhoneLinkConfirmation.confirm(smsCode.trim());
  pendingPhoneLinkConfirmation = null;
}

export function clearPendingPhoneLink() {
  pendingPhoneLinkConfirmation = null;
}
